import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { Settings, Mail, Lock, AlertCircle, Check, ShieldCheck, MessageCircle, User, Camera, Image as ImageIcon, Loader2 } from 'lucide-react';
import { baseAuthApi } from '../../../api/baseAuthApi';
import { useWaCheck, waBadge } from '../../../hooks/useWaCheck';
import WhatsAppNumberForm from '../../../components/dashboard/WhatsAppNumberForm';

// Comprime la imagen a un dataURL chico (máx 512px, webp) por el límite ~1mb del body de
// lealify-base. Mismo patrón que la foto de cliente (ClientAvatar) y que la agenda.
function fileToCompressedDataUrl(file: File, max = 512, quality = 0.85): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer la imagen'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Imagen inválida'));
      img.onload = () => {
        let { width, height } = img;
        if (width >= height && width > max) { height = Math.round(height * max / width); width = max; }
        else if (height > max) { width = Math.round(width * max / height); height = max; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('No se pudo procesar'));
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/webp', quality));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

// --- SUB-COMPONENTE: Mi perfil (foto + nombre + teléfono del PROPIO operador) ---
// Módulo PERFIL compartido: mismos endpoints /me/profile y /me/avatar que usa la agenda
// (la foto del operador no depende del toggle allow_photo, que gobierna la del CLIENTE).
const MyProfileTab: React.FC<{ onError: (m: string) => void; onSuccess: (m: string) => void }> = ({ onError, onSuccess }) => {
  const [profile, setProfile] = useState<{ id: number; name: string; email: string; phone: string | null; avatar_url: string | null } | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const wa = useWaCheck();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [ver, setVer] = useState(0);            // bust de caché tras subir
  const [failed, setFailed] = useState(false);  // sin foto → inicial
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    baseAuthApi.myProfile()
      .then((r) => {
        setProfile(r.profile);
        setName(r.profile.name || '');
        setPhone(r.profile.phone || '');
        setFailed(!r.profile.avatar_url); // solo pide el <img> si la ficha trae URL (sin flash de 404)
      })
      .catch((e: any) => onError(e?.message || 'No se pudo cargar tu perfil'))
      .finally(() => setLoading(false));
  }, []);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file || !profile) return;
    setUploading(true);
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      await baseAuthApi.uploadMyAvatar(dataUrl);
      setFailed(false); setVer(Date.now());
      onSuccess('Foto de perfil actualizada');
    } catch (err: any) {
      onError(err?.message || 'No se pudo subir la foto');
    } finally {
      setUploading(false);
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { onError('El nombre no puede quedar vacío'); return; }
    setSaving(true);
    try {
      const r = await baseAuthApi.updateMyProfile({ name: name.trim(), phone: phone.trim() });
      setProfile((p) => (p ? { ...p, ...r.profile } : p));
      onSuccess('Perfil actualizado correctamente');
    } catch (err: any) {
      onError(err?.message || 'No se pudo guardar el perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-content-3" /></div>;
  }
  if (!profile) return null;

  const initial = (profile.name || '?').trim().charAt(0).toUpperCase() || '?';

  return (
    <div className="animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
      <h3 className="text-lg font-medium text-content flex items-center mb-4">
        <User className="h-5 w-5 mr-2 text-blue-500" />
        Mi Perfil
      </h3>

      {/* Foto */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            disabled={uploading}
            className="relative h-20 w-20 rounded-full active:scale-95 transition-transform focus:outline-none focus:ring-2 focus:ring-accent"
            title="Cambiar foto de perfil"
          >
            {!failed ? (
              <img
                src={baseAuthApi.myAvatarSrc(profile.id, ver)}
                alt={profile.name}
                onError={() => setFailed(true)}
                className="h-20 w-20 rounded-full object-cover"
              />
            ) : (
              <span className="h-20 w-20 rounded-full bg-blue-100 dark:bg-blue-500/20 text-accent flex items-center justify-center text-2xl font-semibold">
                {initial}
              </span>
            )}
            {uploading && (
              <span className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-white animate-spin" />
              </span>
            )}
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} aria-hidden="true" />
              <div className="absolute left-0 top-full mt-1 z-20 w-44 rounded-md bg-surface shadow-lg border border-line py-1">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); cameraRef.current?.click(); }}
                  className="w-full flex items-center px-3 py-2 text-sm text-content hover:bg-surface-2"
                >
                  <Camera className="h-4 w-4 mr-2" /> Tomar foto
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); galleryRef.current?.click(); }}
                  className="w-full flex items-center px-3 py-2 text-sm text-content hover:bg-surface-2"
                >
                  <ImageIcon className="h-4 w-4 mr-2" /> Subir de galería
                </button>
              </div>
            </>
          )}
          <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onPick} />
          <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
        </div>
        <div>
          <p className="text-sm font-medium text-content">{profile.name}</p>
          <p className="text-sm text-content-3">{profile.email}</p>
          <p className="text-xs text-content-3 mt-1">Toca la foto para cambiarla</p>
        </div>
      </div>

      {/* Datos */}
      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label htmlFor="profileName" className="block text-sm font-medium text-content-2">Nombre</label>
          <input
            type="text"
            id="profileName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="mt-1 block w-full rounded-md text-content border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="profilePhone" className="block text-sm font-medium text-content-2">Teléfono</label>
          <input
            type="tel"
            id="profilePhone"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); wa.reset(); }}
            onBlur={() => wa.check(phone)}
            className="mt-1 block w-full rounded-md text-content border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          />
          {(() => { const b = waBadge(wa.state); return b ? <p className={`mt-1 text-xs ${b.className}`}>{b.text}</p> : null; })()}
        </div>
        <div>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar Perfil'}
          </button>
        </div>
      </form>
    </div>
  );
};

const UserSettingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'perfil' | 'email' | 'password' | 'whatsapp'>('perfil');

  // Separate state for email form
  const [emailFormData, setEmailFormData] = useState({
    currentPassword: '',
    newEmail: ''
  });

  // Separate state for password form
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // En base la cuenta del operador se crea por invitación/reset (correo ya validado);
  // no hay verificación de correo aparte como en Firebase.
  const handleEmailUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      await baseAuthApi.changeEmail(emailFormData.currentPassword, emailFormData.newEmail.trim());
      setSuccess('Correo actualizado correctamente. La próxima vez inicia sesión con el nuevo correo.');
      setEmailFormData({ currentPassword: '', newEmail: '' });
    } catch (error: any) {
      console.error('Error updating email:', error);
      setError(error?.message || 'Error al actualizar el correo');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (passwordFormData.newPassword.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      await baseAuthApi.changePassword(passwordFormData.currentPassword, passwordFormData.newPassword);
      setSuccess('Contraseña actualizada correctamente');
      setPasswordFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      console.error('Error updating password:', error);
      setError(error?.message || 'Error al actualizar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="text-2xl font-bold text-content flex items-center">
            <Settings className="h-6 w-6 mr-2 text-accent" />
            Mi cuenta
          </h1>
          <p className="mt-1 text-sm text-content-2">
            Tu nombre, tu foto y tu contraseña de acceso. Lo del negocio (logo, premios, kiosco) está en Mi Negocio.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-500/10 border-l-4 border-red-400 p-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 p-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex">
              <div className="flex-shrink-0">
                <Check className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700 dark:text-green-300">{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Estado de la cuenta */}
        <div className="rounded-lg p-4 flex items-center bg-green-50 dark:bg-green-500/10 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <ShieldCheck className="h-6 w-6 text-green-500 mr-3" />
          <div>
            <h3 className="font-medium text-content">Cuenta verificada</h3>
            <p className="text-sm text-content-2">Tu correo de acceso está confirmado.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="border-b border-line">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('perfil')}
                className={`w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'perfil'
                    ? 'border-accent text-accent'
                    : 'border-transparent text-content-3 hover:text-content-2 hover:border-line'
                }`}
              >
                <User className="h-5 w-5 mx-auto mb-1" />
                <span>Perfil</span>
              </button>
              <button
                onClick={() => setActiveTab('email')}
                className={`w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'email'
                    ? 'border-accent text-accent'
                    : 'border-transparent text-content-3 hover:text-content-2 hover:border-line'
                }`}
              >
                <Mail className="h-5 w-5 mx-auto mb-1" />
                <span>Email</span>
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'password'
                    ? 'border-accent text-accent'
                    : 'border-transparent text-content-3 hover:text-content-2 hover:border-line'
                }`}
              >
                <Lock className="h-5 w-5 mx-auto mb-1" />
                <span>Contraseña</span>
              </button>
              <button
                onClick={() => setActiveTab('whatsapp')}
                className={`w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'whatsapp'
                    ? 'border-accent text-accent'
                    : 'border-transparent text-content-3 hover:text-content-2 hover:border-line'
                }`}
              >
                <MessageCircle className="h-5 w-5 mx-auto mb-1" />
                <span>WhatsApp</span>
              </button>
            </nav>
          </div>

          <div className="px-4 py-5 sm:p-6">
            {activeTab === 'perfil' && (
              <MyProfileTab
                onError={(m) => { setSuccess(null); setError(m); }}
                onSuccess={(m) => { setError(null); setSuccess(m); }}
              />
            )}

            {activeTab === 'email' && (
              <div className="animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
                <h3 className="text-lg font-medium text-content flex items-center mb-4">
                  <Mail className="h-5 w-5 mr-2 text-blue-500" />
                  Cambiar Email
                </h3>
                <form onSubmit={handleEmailUpdate} className="space-y-4">
                  <div>
                    <label htmlFor="currentEmail" className="block text-sm font-medium text-content-2">
                      Email Actual
                    </label>
                    <input
                      type="email"
                      id="currentEmail"
                      value={currentUser?.email || ''}
                      disabled
                      className="mt-1 block w-full rounded-md text-content border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm bg-surface-2"
                    />
                  </div>

                  <div>
                    <label htmlFor="newEmail" className="block text-sm font-medium text-content-2">
                      Nuevo Email
                    </label>
                    <input
                      type="email"
                      id="newEmail"
                      value={emailFormData.newEmail}
                      onChange={(e) => setEmailFormData(prev => ({ ...prev, newEmail: e.target.value }))}
                      required
                      className="mt-1 block w-full rounded-md text-content border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="currentPasswordEmail" className="block text-sm font-medium text-content-2">
                      Contraseña Actual
                    </label>
                    <input
                      type="password"
                      id="currentPasswordEmail"
                      value={emailFormData.currentPassword}
                      onChange={(e) => setEmailFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      required
                      className="mt-1 block w-full rounded-md text-content border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
                    />
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
                    >
                      {loading ? 'Enviando...' : 'Actualizar Email'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'password' && (
              <div className="animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
                <h3 className="text-lg font-medium text-content flex items-center mb-4">
                  <Lock className="h-5 w-5 mr-2 text-blue-500" />
                  Cambiar Contraseña
                </h3>
                <form onSubmit={handlePasswordUpdate} className="space-y-4">
                  <div>
                    <label htmlFor="currentPasswordChange" className="block text-sm font-medium text-content-2">
                      Contraseña Actual
                    </label>
                    <input
                      type="password"
                      id="currentPasswordChange"
                      value={passwordFormData.currentPassword}
                      onChange={(e) => setPasswordFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      required
                      className="mt-1 block w-full rounded-md text-content border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-content-2">
                      Nueva Contraseña
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      value={passwordFormData.newPassword}
                      onChange={(e) => setPasswordFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                      required
                      className="mt-1 block w-full rounded-md text-content border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-content-2">
                      Confirmar Nueva Contraseña
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={passwordFormData.confirmPassword}
                      onChange={(e) => setPasswordFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      className="mt-1 block w-full rounded-md text-content border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
                    />
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
                    >
                      {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'whatsapp' && (
              <div className="animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
                <h3 className="text-lg font-medium text-content flex items-center mb-4">
                  <MessageCircle className="h-5 w-5 mr-2 text-blue-500" />
                  Número de WhatsApp
                </h3>
                <WhatsAppNumberForm showInSettings={true} />
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UserSettingsPage;